import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  ChevronRight as ChevronIcon,
  Refresh as RetryIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";

// ─── Mapping: checkId → targetSlotId (from backend firstSaleDocumentChecks config)
export const CHECK_TO_SLOT: Record<string, string> = {
  po1_checklist: "middleman_po",
  po2_checklist: "factory_po",
  invoice1_checklist: "factory_inv",
  invoice2_checklist: "middleman_inv",
  payment1_checklist: "middleman_payment",
  payment2_checklist: "factory_payment",
  cbs_checklist: "cost_breakdown_sheet",
  assist_checklist: "assist_invoices",
  bol_checklist: "bill_of_lading",
};

export interface CheckItemResult {
  itemId: string;
  status: string;
  detail?: string;
}

export interface InlineChecklistData {
  checkId: string;
  checkName: string;
  status: string;
  detail: string;
  items: CheckItemResult[];
  reasoning?: string;
  citations?: any[];
}

export const ITEM_CFG: Record<string, { color: string; icon: string }> = {
  PASS: { color: tokens.status.success, icon: "\u2713" },
  FAIL: { color: tokens.status.danger, icon: "!" },
  WARNING: { color: tokens.status.warning, icon: "\u2013" },
  NOT_APPLICABLE: { color: tokens.text.disabled, icon: "\u2014" },
  NOT_RUN: { color: tokens.text.disabled, icon: "\u2026" },
};

export const InlineChecklist: React.FC<{
  data: InlineChecklistData;
  reasoning?: string;
  citations?: any[];
  checkId?: string;
  onItemClick?: (item: CheckItemResult, checkName: string, reasoning?: string, citations?: any[]) => void;
  onRerun?: (checkId: string) => void;
  rerunning?: boolean;
}> = ({ data, reasoning, citations, checkId, onItemClick, onRerun, rerunning }) => {
  const [expanded, setExpanded] = useState(false);

  const relevant = data.items.filter(
    (i) => i.status !== "NOT_APPLICABLE" && i.status !== "NOT_RUN"
  );
  const passCount = relevant.filter((i) => i.status === "PASS").length;
  const failCount = relevant.filter((i) => i.status === "FAIL").length;
  const warnCount = relevant.filter((i) => i.status === "WARNING").length;

  const overallColor =
    failCount > 0 ? tokens.status.danger : warnCount > 0 ? tokens.status.warning : tokens.status.success;

  return (
    <Box
      sx={{
        ml: 5,
        mr: 2,
        mb: 1,
        borderRadius: tokens.radius.sm,
        border: `1px solid ${overallColor}20`,
        bgcolor: `${overallColor}04`,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setExpanded(!expanded)}
        sx={{
          px: 1.5,
          py: 0.75,
          cursor: "pointer",
          "&:hover": { bgcolor: `${overallColor}08` },
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            bgcolor: overallColor,
            color: tokens.bg.surface,
            fontWeight: tokens.fontWeight.heavy,
            fontSize: 9,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {failCount > 0 ? "!" : warnCount > 0 ? "\u2013" : "\u2713"}
        </Box>
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            fontWeight: tokens.fontWeight.bold,
            color: tokens.text.primary,
            flex: 1,
          }}
        >
          AI Validation
        </Typography>
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            fontWeight: tokens.fontWeight.semibold,
            color: overallColor,
          }}
        >
          {passCount}/{relevant.length} passing
        </Typography>
        {failCount > 0 && (
          <Chip
            label={`${failCount} failed`}
            size="small"
            sx={{
              fontSize: "10px",
              height: 18,
              bgcolor: `${tokens.status.danger}12`,
              color: tokens.status.danger,
              borderRadius: tokens.radius.sm,
              fontWeight: tokens.fontWeight.semibold,
            }}
          />
        )}
        {warnCount > 0 && (
          <Chip
            label={`${warnCount} warnings`}
            size="small"
            sx={{
              fontSize: "10px",
              height: 18,
              bgcolor: `${tokens.status.warning}12`,
              color: tokens.status.warning,
              borderRadius: tokens.radius.sm,
              fontWeight: tokens.fontWeight.semibold,
            }}
          />
        )}
        {checkId && onRerun && (
          <Tooltip title="Re-run this document check">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onRerun(checkId); }}
              disabled={rerunning}
              sx={{
                color: tokens.text.muted,
                p: 0.25,
                "&:hover": { color: tokens.brand.primary },
                "& svg": rerunning ? { animation: "spin 1s linear infinite" } : {},
                "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
              }}
            >
              <RetryIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        <ChevronIcon
          sx={{
            fontSize: 16,
            color: tokens.text.muted,
            transform: expanded ? "rotate(90deg)" : "none",
            transition: "transform 200ms ease",
          }}
        />
      </Stack>

      {/* Expanded items */}
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1, pt: 0.25 }}>
          <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.secondary, mb: 0.75, lineHeight: 1.4 }}>
            {data.detail}
          </Typography>
          <Stack spacing={0.4}>
            {data.items.map((item) => {
              if (item.status === "NOT_RUN") return null;
              const cfg = ITEM_CFG[item.status] || ITEM_CFG.NOT_RUN;
              return (
                <Stack
                  key={item.itemId}
                  direction="row"
                  spacing={0.75}
                  alignItems="flex-start"
                  onClick={() => {
                    if ((item.status === "FAIL" || item.status === "WARNING") && onItemClick) {
                      onItemClick(item, data.checkName, reasoning, citations);
                    }
                  }}
                  sx={{
                    cursor: item.status === "FAIL" || item.status === "WARNING" ? "pointer" : "default",
                    borderRadius: tokens.radius.sm,
                    px: 0.5,
                    mx: -0.5,
                    "&:hover": (item.status === "FAIL" || item.status === "WARNING")
                      ? { bgcolor: `${cfg.color}08` }
                      : {},
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: cfg.color,
                      color: tokens.bg.surface,
                      fontWeight: tokens.fontWeight.heavy,
                      fontSize: 7,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: 0.2,
                    }}
                  >
                    {cfg.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: tokens.fontSize["2xs"],
                        fontWeight: tokens.fontWeight.semibold,
                        color: cfg.color,
                      }}
                    >
                      {item.itemId
                        .replace(/^(po[12]|inv[12]|pay[12]|cbs|ast|bol)_/, "")
                        .replace(/_/g, " ")
                        .replace(/^\w/, (c: string) => c.toUpperCase())}
                    </Typography>
                    {item.detail && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: tokens.fontSize["2xs"],
                          color: tokens.text.muted,
                          ml: 0.75,
                        }}
                      >
                        — {item.detail}
                      </Typography>
                    )}
                  </Box>
                  {(item.status === "FAIL" || item.status === "WARNING") && (
                    <ChevronIcon sx={{ fontSize: 14, color: tokens.text.muted, flexShrink: 0, mt: 0.1 }} />
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};
